import type { RefObject } from 'react';

import type { KeyboardAction } from '../user-interfaces/hooks/use-keyboard-shortcuts';

import { useEffect, useState } from 'react';

import { KeyboardIndicators } from './keyboard-indicators';
import { PauseIcon } from '../../icons/pause';
import { BigPlayButton } from '../big-play-button/big-play-button';

import styles from './overlay.module.css';

type Props = {
  isPlaying?: boolean;
  isEnded?: boolean;
  isTrimmable?: boolean;
  volume: number;
  isMuted?: boolean;
  wrapperRef: RefObject<HTMLElement>;
  onClick?: () => void;
  lastKeyboardAction?: KeyboardAction;
  enableBigPlayButton?: boolean;
};

function isBigPlayButtonVisible(
  isTrimmable: boolean,
  enableBigPlayButton: boolean,
  isPlaying: boolean,
  isEnded: boolean
): boolean {
  // special flags to hide the big play button
  if (!enableBigPlayButton || isTrimmable) {
    return false;
  }

  // if the player is playing, hide the big play button
  let bigPlayBtnVisible = !isPlaying;

  // playback endend => show big play button
  if (isEnded) {
    bigPlayBtnVisible = true;
  }

  return bigPlayBtnVisible;
}

export function Overlay({
  isPlaying = false,
  isEnded = false,
  isTrimmable = false,
  volume,
  isMuted,
  wrapperRef,
  onClick,
  lastKeyboardAction,
  enableBigPlayButton = true,
}: Props) {
  const [bigPlayButtonVisible, setBigPlayButtonVisible] = useState(() => {
    // BigPlayBtn should initially only be visible
    return isBigPlayButtonVisible(isTrimmable, enableBigPlayButton, isPlaying, isEnded);
  });
  const [pauseIconVisible, setPauseIconVisible] = useState(false);

  const handlePauseIconAnimationEnd = () => {
    setPauseIconVisible(false);
  };

  useEffect(() => {
    if (!enableBigPlayButton || isTrimmable) {
      return;
    }

    if (isPlaying) {
      // if the player is playing, hide the big play button until video ends
      setBigPlayButtonVisible(false);
    }

    if (isEnded) {
      // playback ended => show big play button
      setBigPlayButtonVisible(true);
    }

    if (!bigPlayButtonVisible && (isPlaying || isEnded)) {
      setPauseIconVisible(true);
    }
  }, [enableBigPlayButton, bigPlayButtonVisible, isPlaying, isEnded, isTrimmable]);

  return (
    <>
      <div className={styles.wrapper} onClickCapture={onClick} data-testid="overlay">
        {pauseIconVisible && <PauseIcon width="108" height="126" onAnimationEnd={handlePauseIconAnimationEnd} />}
        {!bigPlayButtonVisible && (
          <KeyboardIndicators volume={volume} isMuted={isMuted} lastKeyboardAction={lastKeyboardAction} />
        )}
      </div>

      {bigPlayButtonVisible && (
        <BigPlayButton
          onClick={() => {
            onClick?.();
            // the player will lose the focus when the big play button is clicked
            // so we need to focus the wrapper again to allow keyboard navigation
            wrapperRef.current?.focus();
          }}
        />
      )}
    </>
  );
}
